import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Info } from "lucide-react";

interface InfoHintProps {
  /** Short noun phrase describing the content, used for the accessible name. */
  label: string;
  children: ReactNode;
  /** Horizontal anchoring of the panel relative to the trigger. */
  align?: "start" | "end";
  className?: string;
}

/**
 * Reveals reference copy that is read once and then only occasionally re-checked.
 * Destructive-action warnings stay inline; they are read at the moment of the action.
 */
export function InfoHint({ label, children, align = "start", className }: InfoHintProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      wrapperRef.current?.querySelector("button")?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <span className={className ? `info-hint ${className}` : "info-hint"} ref={wrapperRef}>
      <button
        type="button"
        className="info-hint-trigger"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={`About ${label}`}
        onClick={() => setOpen((current) => !current)}
      >
        <Info size={15} aria-hidden="true" />
      </button>
      {open ? (
        <span className={align === "end" ? "info-hint-panel info-hint-end" : "info-hint-panel"} id={panelId} role="note">
          {children}
        </span>
      ) : null}
    </span>
  );
}
